import './ReservationCardBlock.scss';

import { Icon, Loader, Timer, Typography } from '@modulor/react';
import { CheckmarkIcon } from 'assets/CheckmarkIcon';
import { FileErrorIcon } from 'assets/FileErrorIcon';
import { FileIcon } from 'assets/FileIcon';
import { useSkeletonTranslation } from 'hooks/useSkeletonTranslation';
import LoadedFileCell from 'Modulor/components/FileUpload/LoadedFileCell';
import type { ChangeEvent, FC, RefObject } from 'react';
import { useState } from 'react';

import { RESERVATION_KEYS } from '../../translationKeys';
import { isCardProofRejected, ReservationCardStatus } from '../../types';

const ACCEPT = '.pdf';
const COUNTDOWN_SECONDS = 180;

interface ReservationCardBlockProps {
  status: ReservationCardStatus | null;
  inputRef: RefObject<HTMLInputElement>;
  currentFile: File | null;
  /** False while the card field is still being filled in — shows the "checking" state. */
  isCardReady: boolean;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onReset: () => void;
  onOpenPicker: () => void;
}

/**
 * Inline card-verification block with the five states from the design.
 *
 * This duplicates the composition of `CardVerificationController`'s `FileUpload` on purpose: that
 * component belongs to the separate `kyc_card_verification` feature, and either feature may be
 * removed independently, so neither may depend on the other. Only genuinely generic pieces are
 * shared — the icons from `assets/` and `LoadedFileCell`.
 */
export const ReservationCardBlock: FC<ReservationCardBlockProps> = ({
  status,
  inputRef,
  currentFile,
  isCardReady,
  onFileChange,
  onReset,
  onOpenPicker,
}) => {
  const { skeletonT } = useSkeletonTranslation();
  const [countdownKey, setCountdownKey] = useState(0);

  const renderBody = (titleKey: string, contentKey: string) => (
    <div className="reservation-card-block__text">
      <Typography variant="body-regular" color="text-title">
        {skeletonT(titleKey)}
      </Typography>
      <Typography variant="caption-1-regular" color="text-body">
        {skeletonT(contentKey)}
      </Typography>
    </div>
  );

  // Still collecting card details — nothing to verify yet.
  if (!isCardReady) {
    return (
      <div className="reservation-card-block">
        {renderBody(RESERVATION_KEYS.blockCheckingTitle, RESERVATION_KEYS.blockCheckingBody)}
        <div className="reservation-card-block__aside">
          <Loader size="small" variant="circle" />
        </div>
      </div>
    );
  }

  if (status === ReservationCardStatus.verified) {
    return (
      <div className="reservation-card-block reservation-card-block--verified">
        <div className="reservation-card-block__icon">
          <CheckmarkIcon data-testid="reservation-card-verified" />
        </div>
        {renderBody(RESERVATION_KEYS.blockVerifiedTitle, RESERVATION_KEYS.blockVerifiedBody)}
      </div>
    );
  }

  if (status === ReservationCardStatus.inProgress) {
    return (
      <div className="reservation-card-block">
        {renderBody(RESERVATION_KEYS.blockPendingTitle, RESERVATION_KEYS.blockPendingBody)}
        <div className="reservation-card-block__aside">
          <Timer
            key={countdownKey}
            duration={COUNTDOWN_SECONDS}
            onComplete={() => setCountdownKey((prev) => prev + 1)}
            color="text-body"
          />
        </div>
      </div>
    );
  }

  // Contract: a verification result of 3 or 4 both render red.
  const hasFailed = isCardProofRejected(status);

  return (
    <div
      className="reservation-card-block reservation-card-block--actionable"
      onClick={currentFile ? undefined : onOpenPicker}
      role="button"
      tabIndex={currentFile ? -1 : 0}
    >
      <div className="reservation-card-block__icon">{hasFailed ? <FileErrorIcon /> : <FileIcon />}</div>
      {renderBody(
        hasFailed ? RESERVATION_KEYS.blockFailedTitle : RESERVATION_KEYS.blockConfirmTitle,
        hasFailed ? RESERVATION_KEYS.blockFailedBody : RESERVATION_KEYS.blockConfirmBody,
      )}
      <input
        type="file"
        ref={inputRef}
        onChange={onFileChange}
        accept={ACCEPT}
        className="reservation-card-block__input"
      />
      <div className="reservation-card-block__aside">
        {currentFile instanceof File ? (
          <LoadedFileCell uploadedFile={currentFile} handleResetFile={onReset} />
        ) : (
          <Icon name="plus" size={24} />
        )}
      </div>
    </div>
  );
};
